import { Component, EventEmitter, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash';
import { Criteria } from '../../../models/criteria';
import { CriteriaType } from '../../../models/criteriaType';
import { TimeCriteria } from '../../../models/timeCriteria'
import { Dictionary } from 'lodash';
import { TimeCriteriaPipe } from '../../../shared/pipes/timeCriteria';
import { ListDefinitionData, ListDefinitionService } from '../../../shared/services/listDefinition.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'app-schedule-criteria-form',
    templateUrl: './schedule-criteria-form.component.html',
    styleUrls: ['./schedule-criteria-form.component.css'],
    standalone: false
})
export class ScheduleCriteriaFormComponent implements OnInit {

  @Output() saved = new EventEmitter<any>();
  scheduleCriteriaForm: UntypedFormGroup;
  scheduleAttributeOptions: Array<ListDefinitionData>;
  scheduleCriteria: Criteria;
  existingScheduleCriteria: Criteria;
  scheduleAttributes: Array<any> = [];
  scheduleAttributeKeys: Observable<Array<string>>;
  scheduleAttributeValues: Observable<Array<string>>;
  scheduleAttributeKeyOptions: Array<string>;
  scheduleAttributeValueOptions: Array<string>;
  CriteriaType: typeof CriteriaType = CriteriaType;
  TimeCriteria: typeof TimeCriteria = TimeCriteria;
  scheduleAttributesForm = new UntypedFormGroup({
    property: new UntypedFormControl(),
    value: new UntypedFormControl()
  })
  scheduleAttributeKeysControl = new FormControl('', Validators.required);
  scheduleAttributeValuesControl = new FormControl('', Validators.required);
  hasDuplicateKey: boolean;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly snackBar: MatSnackBar,
    private readonly listDefinitionService: ListDefinitionService) { }

  ngOnInit(): void {
    this.scheduleCriteriaForm = this.formBuilder.group({
      type: [CriteriaType.Recurring, Validators.required],
      time: [TimeCriteria.None, Validators.required],
      distance: [0],
      engineHours: [0],
      count: [0]
    });

    this.scheduleAttributesForm = this.formBuilder.group({
      property: this.scheduleAttributeKeysControl,
      value: this.scheduleAttributeValuesControl,
    });
    this.loadExistingScheduleCriteria();
    this.listDefinitionService.getListServiceByArea('scheduleCriteria').subscribe(res => {
      if (res) {
        this.scheduleAttributeOptions = res[0].data;
        this.scheduleAttributeKeyOptions = [...new Set(this.scheduleAttributeOptions.map(x => {
          return x.key;
        }))];
        this.scheduleAttributeKeys = this.scheduleAttributeKeysControl.valueChanges.pipe(
          startWith(''),
          map(value => this._filter(value || '')),
        );
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.existingScheduleCriteria && changes.existingScheduleCriteria.currentValue) {
      this.loadExistingScheduleCriteria();
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    this.scheduleAttributeValueOptions =
      this.scheduleAttributeOptions
        .filter(x => {
          return x.key === value;
        })
        .map(y => {
          return y.value
        });

    var exists = this.scheduleAttributes.find(x => x.key.toLowerCase() === value.toLowerCase());
    if (exists) {
      this.hasDuplicateKey = true;
      this.scheduleAttributesForm.controls['property'].setErrors({'hasDuplicateKey': true})
    }
    else {
      this.hasDuplicateKey = false;
      this.scheduleAttributesForm.controls['property'].setErrors(null);
    }

    return this.scheduleAttributeKeyOptions.filter(option => option.toLowerCase().includes(filterValue));
  }
  loadExistingScheduleCriteria(): void {
    if (this.existingScheduleCriteria) {
      const scheduleCriteria = cloneDeep(this.existingScheduleCriteria);
      this.scheduleCriteriaForm.controls['type'].setValue(scheduleCriteria.type);
      let timeValue = new TimeCriteriaPipe().transform(scheduleCriteria.time);
      this.scheduleCriteriaForm.controls['time'].setValue(TimeCriteria[timeValue]);
      this.scheduleCriteriaForm.controls['distance'].setValue(scheduleCriteria.distance);
      this.scheduleCriteriaForm.controls['engineHours'].setValue(scheduleCriteria.engineHours);
      this.scheduleCriteriaForm.controls['count'].setValue(scheduleCriteria.count);

      this.scheduleAttributes = Object.keys(scheduleCriteria.schedulingAttributes).map(x => {
        return { key: x, value: scheduleCriteria.schedulingAttributes[x] };
      })

    }
  }
  addScheduleAttribute(): void {
    if (!this.scheduleAttributesForm.invalid) {
      const property = this.scheduleAttributesForm.get('property')?.value;
      const value = this.scheduleAttributesForm.get('value')?.value;
      var exists = this.scheduleAttributes.find(x => x.key.toLowerCase() === property.toLowerCase());
      if (!exists) {
        this.hasDuplicateKey = false;
        this.scheduleAttributesForm.controls['property'].setErrors(null);
        this.scheduleAttributes.push({
          key: property,
          value: value
        });
        //clear form
        this.clearScheduleAttributeForm();
      }
      else {
        this.hasDuplicateKey = true;
        this.scheduleAttributesForm.controls['property'].setErrors({'hasDuplicateKey': true})
      }
    }
  }
  clearScheduleAttributeForm(){
    this.scheduleAttributesForm.controls['property'].setValue('');
    this.scheduleAttributesForm.controls['value'].setValue('');
    this.scheduleAttributesForm.controls['value'].setErrors(null);
  }
  removeScheduleAttribute(attribute: any): void {
    this.scheduleAttributes = this.scheduleAttributes.filter(x => {
      return x !== attribute;
    })
  }
  add() {
    if (this.scheduleCriteriaForm.invalid) {
      this.scheduleCriteriaForm.markAllAsTouched();
      const notification = this.translate.instant('@missingValues');
      const action = this.translate.instant('@ok');
      this.snackBar.open(notification, action, { duration: 8555 });
      return;
    }
    const property = this.scheduleAttributesForm.get('property')?.value;
    const value = this.scheduleAttributesForm.get('value')?.value;
    if(property || value){
      const notification = this.translate.instant('You have unsubmitted scheduling attributes');
      const action = this.translate.instant('@ok');
      this.snackBar.open(notification, action, { duration: 8555 });
      return;
    }
    var toReturn = new Criteria();
    //get properties from form
    const type = this.scheduleCriteriaForm.get('type')?.value;
    const time = this.scheduleCriteriaForm.get('time')?.value + '.00:00:00';
    const distance = this.scheduleCriteriaForm.get('distance')?.value ?? 0;
    const engineHours = this.scheduleCriteriaForm.get('engineHours')?.value ?? 0;
    const count = this.scheduleCriteriaForm.get('count')?.value ?? 0;

    toReturn.type = type;
    toReturn.time = time;
    toReturn.distance = distance;
    toReturn.engineHours = engineHours;
    toReturn.count = count;
    const tempObject = {};
    this.scheduleAttributes.forEach(x => {
      tempObject[x.key] = x.value;
    });
    toReturn.schedulingAttributes = tempObject;

    this.saved.emit(toReturn);
    this.clearForm();
  }
  clearForm() {
    this.scheduleCriteriaForm = this.formBuilder.group({
      type: [''],
      time: [''],
      distance: [''],
      engineHours: [''],
      count: ['']
    });
    this.scheduleAttributesForm = this.formBuilder.group({
      property: [''],
      value: [''],
    });
  }
  cancel() {
    this.clearForm();
    this.saved.next(null);
  }

}
